import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import './App.css';
import { TROGDOR_TITLE, TROGDOR_DRAGON, TROGDOR_TAGLINE } from './TrogdorArt';
import RegisterForm from './RegisterForm';
import LoginForm from './LoginForm';
import ProfileMenu from './ProfileMenu';
import ChannelPicker from './ChannelPicker';
import NavBar from './NavBar';
import Policies from './Policies';
import DraftPolicies from './DraftPolicies';
import Channels from './Channels';

// Known channel options for the user config screen - static for now; a
// later pass will source this list (and the saved selections) from a
// backend endpoint instead of hardcoding it here.
const CHANNEL_OPTIONS = ['main', 'conda-forge', 'pypi/mirror'];

export default function App() {
  const auth = useAuth();
  const [view, setView] = useState('home'); // home | register | login
  const [loggingOut, setLoggingOut] = useState(false);
  const [signedInView, setSignedInView] = useState('welcome'); // welcome | profile
  const [activeTab, setActiveTab] = useState('policies'); // policies | draft-policies | channels

  // The full ASCII banner + dragon art + tagline ("the splash") is only
  // shown on the initial logged-out home screen - login, register, and
  // every authenticated view get the small header instead (see
  // `.app-header__brand`), which is also where the profile menu lives
  // once signed in.
  const showSplash = !auth.isLoading && !auth.isAuthenticated && view === 'home';

  // "Default channels" must always be a subset of "allowed channels": adding
  // a default channel also allows it, and removing an allowed channel also
  // un-defaults it. Removing a default channel has no effect on allowed
  // channels, and allowing a channel has no effect on defaults.
  const [defaultChannels, setDefaultChannels] = useState(['main']);
  const [allowedChannels, setAllowedChannels] = useState(['main']);
  const [allowDrySolve, setAllowDrySolve] = useState(true);
  const [saveState, setSaveState] = useState('idle'); // idle | saving
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [toastKey, setToastKey] = useState(0);

  const username = auth.user?.profile?.preferred_username
    ?? auth.user?.profile?.username
    ?? auth.user?.profile?.sub;

  async function handleLogout() {
    setLoggingOut(true);
    try {
      // No redirect: this grant was issued inline (see LoginForm/the
      // "password" grant type), so there's no browser session at the
      // auth service to end - just revoke the tokens server-side and
      // drop them locally.
      await auth.revokeTokens();
    } catch {
      // Best-effort; still clear the local session below either way.
    } finally {
      await auth.removeUser();
      setView('home');
      setSignedInView('welcome');
      setLoggingOut(false);
    }
  }

  function handleAddDefaultChannel(channel) {
    setDefaultChannels((prev) => (prev.includes(channel) ? prev : [...prev, channel]));
    // A default channel is implicitly allowed too.
    setAllowedChannels((prev) => (prev.includes(channel) ? prev : [...prev, channel]));
  }

  function handleRemoveDefaultChannel(channel) {
    setDefaultChannels((prev) => prev.filter((c) => c !== channel));
  }

  function handleAddAllowedChannel(channel) {
    setAllowedChannels((prev) => (prev.includes(channel) ? prev : [...prev, channel]));
  }

  function handleRemoveAllowedChannel(channel) {
    setAllowedChannels((prev) => prev.filter((c) => c !== channel));
    // Can't default to a channel that's no longer allowed.
    setDefaultChannels((prev) => prev.filter((c) => c !== channel));
  }

  // Bumps the key so a second failure while the previous toast is still
  // fading remounts (and thus restarts) the animation instead of being
  // a no-op re-render. Shared by every error path that should raise the
  // bottom error-toast, not just the config save below.
  function triggerErrorToast() {
    setToastKey((prev) => prev + 1);
    setShowErrorToast(true);
  }

  async function handleSaveConfig() {
    setSaveState('saving');
    try {
      const response = await fetch('/api/config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.user?.access_token}`,
        },
        body: JSON.stringify({
          default_channels: defaultChannels,
          allowed_channels: allowedChannels,
          dry_solve: allowDrySolve,
        }),
      });
      if (!response.ok) {
        throw new Error(`save failed with status ${response.status}`);
      }
    } catch {
      triggerErrorToast();
    } finally {
      setSaveState('idle');
    }
  }

  return (
    <div className="crt">
      <div className="scanlines" aria-hidden="true" />
      <main className="App">
        <header className="app-header">
          {showSplash ? (
            <pre className="trogdor-title" aria-label="TROGDOR">
              {TROGDOR_TITLE}
            </pre>
          ) : (
            <p className="app-header__brand">TROGDOR</p>
          )}
          {!auth.isLoading && auth.isAuthenticated && (
            <div className="app-header__profile">
              <ProfileMenu
                onSelectProfile={() => setSignedInView('profile')}
                onLogout={handleLogout}
                loggingOut={loggingOut}
              />
            </div>
          )}
        </header>

        {showSplash && (
          <>
            <pre className="trogdor-dragon" aria-hidden="true">
              {TROGDOR_DRAGON}
            </pre>
            <p className="tagline">{TROGDOR_TAGLINE}</p>
          </>
        )}

        {auth.isLoading && <p className="dim-line">AUTHENTICATING...</p>}

        {!auth.isLoading && auth.isAuthenticated && signedInView === 'welcome' && (
          <>
            <p className="success-line">
              [ LOGGED IN ] welcome back, {username}. the countryside awaits.
            </p>

            <NavBar activeTab={activeTab} onTabChange={setActiveTab} />

            {activeTab === 'policies' && <Policies onError={triggerErrorToast} />}
            {activeTab === 'draft-policies' && <DraftPolicies onError={triggerErrorToast} />}
            {activeTab === 'channels' && <Channels onError={triggerErrorToast} />}
          </>
        )}

        {!auth.isLoading && auth.isAuthenticated && signedInView === 'profile' && (
          <div className="terminal-box profile-page config-page">
            <h2 className="form-title">PROFILE</h2>
            <p className="profile-page-label">NAME</p>
            <p className="profile-page-name">{username}</p>

            <ChannelPicker
              label="DEFAULT CHANNELS"
              options={CHANNEL_OPTIONS}
              selected={defaultChannels}
              onAdd={handleAddDefaultChannel}
              onRemove={handleRemoveDefaultChannel}
            />

            <ChannelPicker
              label="ALLOWED CHANNELS"
              options={CHANNEL_OPTIONS}
              selected={allowedChannels}
              onAdd={handleAddAllowedChannel}
              onRemove={handleRemoveAllowedChannel}
            />

            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={allowDrySolve}
                onChange={(event) => setAllowDrySolve(event.target.checked)}
              />
              Allow Pypi Dry Solve
            </label>

            <div className="button-row button-row--split">
              <button
                type="button"
                className="retro-button retro-button--ghost"
                onClick={() => setSignedInView('welcome')}
              >
                &laquo; BACK
              </button>
              <button
                type="button"
                className="retro-button"
                onClick={handleSaveConfig}
                disabled={saveState === 'saving'}
              >
                {saveState === 'saving' ? 'SAVING...' : 'SAVE'}
              </button>
            </div>
          </div>
        )}

        {!auth.isLoading && !auth.isAuthenticated && view === 'home' && (
          <div className="button-row">
            <button
              type="button"
              className="retro-button"
              onClick={() => setView('login')}
            >
              LOGIN
            </button>
            <button
              type="button"
              className="retro-button retro-button--accent"
              onClick={() => setView('register')}
            >
              REGISTER
            </button>
          </div>
        )}

        {!auth.isLoading && !auth.isAuthenticated && view === 'login' && (
          <LoginForm
            onCancel={() => setView('home')}
            onSuccess={() => setView('home')}
          />
        )}

        {!auth.isLoading && !auth.isAuthenticated && view === 'register' && (
          <RegisterForm onCancel={() => setView('home')} />
        )}

        <footer className="footer">grim_repo &middot; est. this repo</footer>
      </main>

      {showErrorToast && (
        <div
          key={toastKey}
          className="error-toast"
          role="alert"
          onAnimationEnd={() => setShowErrorToast(false)}
        >
          <span className="error-toast__icon" role="img" aria-label="Muscle">
            💪
          </span>
          <span className="error-toast__text">ERROR</span>
        </div>
      )}
    </div>
  );
}
